import { Layer } from "../../../types/publication";

import { RenderRow } from "./reorder-utils";

/**
 * Finds the map row (`data-map-id`, see `left-map-list.tsx`) under a drag pointer, if any. Uses
 * `elementsFromPoint` instead of `elementFromPoint` because the dragged item itself is always the
 * topmost element at the pointer.
 */
export function getMapElementAtPoint(x: number, y: number): HTMLElement | null {
  const elements = document.elementsFromPoint(x, y);
  for (const element of elements) {
    const mapElement = element.closest<HTMLElement>("[data-map-id]");
    if (mapElement) return mapElement;
  }
  return null;
}

/**
 * Finds where a multi-select drag's block of selected rows should land, based on the pointer's Y
 * position against the real unselected rows in the DOM (`data-row-id`). Framer-motion's own
 * single-swap `onReorder` can't move a block of selected rows without ever reversing two adjacent
 * selected rows, so multi-select drags are positioned this way instead (see `use-layer-drag.ts`).
 */
export function findDropIndex(
  container: HTMLElement,
  pointerY: number,
  selectedIdSet: Set<string>,
  remainingIds: string[]
): number {
  const rowElements = container.querySelectorAll<HTMLElement>("[data-row-id]");

  for (const element of rowElements) {
    const id = element.dataset.rowId;
    if (!id || selectedIdSet.has(id)) continue;

    const rect = element.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    if (pointerY < midpoint) {
      const index = remainingIds.indexOf(id);
      return index === -1 ? remainingIds.length : index;
    }
  }

  // Pointer is below all rows, so append the dragged block at the end.
  return remainingIds.length;
}

/** Reinserts a selected block of ids into the unselected ids at `insertIndex`. */
export function insertBlockAt(
  remainingIds: string[],
  selectedIds: string[],
  insertIndex: number
): string[] {
  return [
    ...remainingIds.slice(0, insertIndex),
    ...selectedIds,
    ...remainingIds.slice(insertIndex),
  ];
}

/** Ids of the currently selected layer rows, in render order. */
export function getSelectedLayerIds(rows: RenderRow[]): string[] {
  return rows
    .filter(
      (row): row is { kind: "layer"; layer: Layer } => row.kind === "layer" && row.layer.selected
    )
    .map((row) => row.layer.id);
}
